import React, { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import LoopIcon from "@mui/icons-material/Loop";
import { Link as MuiLink } from "@mui/material";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";

import {
  Alert,
  Chip,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSelector } from "react-redux";
import { capitalizeEachWords } from "../../../../../utils/helpers";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  setPage,
  setRowsPerPage,
} from "../../../../../features/brandConfigurationReportSlice";
import { selectUserData } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { formatLastRunDate } from "../../utilitiesOfBrandConfig/helperOfBrandConfig";
import { useBrandProperties } from "../../brandPropertiesHook/useBrandProperties";

interface Props {
  tableHeadings: { [key: string]: string };
}

const tableHeaders = [
  { id: 1, KEY: "brand", label: "Brand", sortable: true },
  {
    id: 2,
    KEY: "backend_interface_name",
    label: "Affiliate System",
    sortable: true,
  },
  { id: 3, KEY: "ui_site_name", label: "Website", sortable: true },
  {
    id: 4,
    KEY: "integration_status",
    label: "Integration Status",
    sortable: true,
  },
  { id: 5, KEY: "stuck_at", label: "Stuck At", sortable: true },
  { id: 6, KEY: "reason", label: "Reason", sortable: true },
  { id: 7, KEY: "last_run", label: "Last Run", sortable: true },

  { id: 8, KEY: "action", label: "Action", sortable: false },
];

const BrandConfigurationReportTableData = ({ tableHeadings }: Props) => {
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sort, setSort] = useState<{
    keytoSort: string | null;
    direction: "asc" | "desc" | null;
  }>({ keytoSort: "brands__name", direction: "asc" });
  const headCells = Object.entries(tableHeadings);

  const hasBrandConfigEditPermission = CheckPermissions(
    userData?.data?.products?.partners,
    "data_intregrations",
    "configure_new_brand",
  );

  const {
    brandConfigurationReportList: modifiedbrandConfiguration,
    selectedBrandName,
    selectedIntegrationStatus,
    selectedStuckAt,
    selectedReason,
    selectedAffiliatedSystem,
    selectedWebsite,
    selectedLastRun,
    page,
    rowsPerPage,
  } = useSelector((state: any) => state.brandConfigurationReport);

  const { brandIntegrationProcess } = useBrandProperties();

  //-- Filtering Data
  const filteredRows = useMemo(() => {
    return (sortedData || []).filter((row: any) => {
      const brandNameMatch = selectedBrandName
        ? row.brand
            ?.toLowerCase()
            .trim()
            .includes(selectedBrandName.toLowerCase().trim())
        : true;

      const integrationStatusMatch =
        selectedIntegrationStatus.length > 0
          ? selectedIntegrationStatus.includes(row.integration_status)
          : true;

      const stuckAtMatch =
        selectedStuckAt.length > 0
          ? selectedStuckAt.includes(
              row.additional_information?.step
                ? capitalizeEachWords(row.additional_information.step)
                : "",
            )
          : true;

      const reasonMatch =
        selectedReason.length > 0
          ? selectedReason.includes(row.additional_information?.reason)
          : true;

      const affiliatedSystemMatch =
        selectedAffiliatedSystem.length > 0
          ? selectedAffiliatedSystem.includes(row.backend_interface_name)
          : true;

      const websiteMatch =
        selectedWebsite.length > 0
          ? selectedWebsite.includes(row.ui_site_name)
          : true;

      const lastRunMatch =
        selectedLastRun &&
        selectedLastRun.length === 2 &&
        selectedLastRun[0] &&
        selectedLastRun[1]
          ? selectedLastRun[0] === selectedLastRun[1] // If both dates are the same, check for equality
            ? new Date(row.additional_information?.last_run).toDateString() ===
              new Date(selectedLastRun[0]).toDateString()
            : new Date(row.additional_information?.last_run) >=
                new Date(selectedLastRun[0]) &&
              new Date(row.additional_information?.last_run) <=
                new Date(new Date(selectedLastRun[1]).setHours(23, 59, 59, 999))
          : true;

      return (
        brandNameMatch &&
        integrationStatusMatch &&
        stuckAtMatch &&
        reasonMatch &&
        affiliatedSystemMatch &&
        websiteMatch &&
        lastRunMatch
      );
    });
  }, [
    sortedData,
    selectedBrandName,
    selectedIntegrationStatus,
    selectedStuckAt,
    selectedReason,
    selectedAffiliatedSystem,
    selectedWebsite,
    selectedLastRun,
  ]);

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      // If no sort is applied, return original order
      if (!sort.keytoSort || !sort.direction) {
        return [...arrayToSort]; // Return a copy of original order
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined || keytoSort === null) return 0;

        let aValue;
        let bValue;

        if (keytoSort === "stuck_at") {
          aValue = a.additional_information?.step;
          bValue = b.additional_information?.step;
        } else if (keytoSort === "reason") {
          aValue = a.additional_information?.reason;
          bValue = b.additional_information?.reason;
        } else if (keytoSort === "last_run") {
          aValue = a.additional_information?.last_run;
          bValue = b.additional_information?.last_run;
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  useEffect(() => {
    if (!modifiedbrandConfiguration || modifiedbrandConfiguration.length === 0)
      return;

    // Then sort the data with accurate counts
    const sortedRows = getSortedData(modifiedbrandConfiguration || []);

    setSortedData(sortedRows);
  }, [modifiedbrandConfiguration, getSortedData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    setSort((prevSort) => {
      // If clicking the same column
      if (header.KEY === prevSort.keytoSort) {
        // Cycle through: asc -> desc -> no sort
        if (prevSort.direction === "asc") {
          return { keytoSort: header.KEY, direction: "desc" };
        } else if (prevSort.direction === "desc") {
          // Third click: clear sort
          return { keytoSort: null, direction: null };
        }
      }

      // If clicking a different column or no sort is active, start with asc
      return { keytoSort: header.KEY, direction: "asc" };
    });
  };

  const handleOverrite = (brandId: any) => {
    brandIntegrationProcess(brandId);
  };

  useEffect(() => {
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    dispatch(setRowsPerPage(10));
    // eslint-disable-next-line
  }, [
    selectedBrandName,
    selectedIntegrationStatus,
    selectedStuckAt,
    selectedReason,
    selectedAffiliatedSystem,
    selectedWebsite,
    selectedLastRun,
  ]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <>
      <Paper sx={{ boxShadow: "none" }}>
        <TableContainer
        //component={Paper}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY && sort.direction
                          ? sort.direction
                          : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                      hideSortIcon={false}
                      IconComponent={() => {
                        if (!header.sortable) return null;

                        // Show bidirectional icon when no sort is active on this column
                        if (sort.keytoSort !== header.KEY) {
                          return (
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "18px",
                                opacity: 0.5,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              ⇅
                            </span>
                          );
                        }

                        // Show up or down arrow when this column is sorted
                        return (
                          <span
                            style={{
                              marginLeft: "4px",
                              fontSize: "18px",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {sort.direction === "asc" ? "↑" : "↓"}
                          </span>
                        );
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: header.sortable
                            ? "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                        },
                      }}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row: any) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {headCells.map(([key], cellIndex) => (
                        <TableCell key={cellIndex} align="left">
                          {key === "brand" ? (
                            <> {row[key] ? row[key] : ""}</>
                          ) : key === "backend_interface_name" ? (
                            <> {row[key] ? row[key] : ""}</>
                          ) : key === "ui_site_name" ? (
                            <>
                              <div className="chip-data">
                                {/* {Array.isArray(row?.ui_site_name) &&
                                  row.ui_site_name.length > 0 && (
                                    <> */}
                                {/* Display first chip */}
                                <Chip
                                  label={row.ui_site_name}
                                  sx={{
                                    backgroundColor: "#7B1FA2",
                                    color: "#ffffff",
                                    height: "26px",
                                  }}
                                />
                                {/* Display second chip if available */}
                                {/* {row.ui_site_name.length > 1 && (
                                        <Chip
                                          label={row.ui_site_name[1]}
                                          sx={{
                                            backgroundColor: "#7B1FA2",
                                            color: "#ffffff",
                                            height: "26px",
                                            ml: "1px",
                                          }}
                                        />
                                      )}
                                      {/* AvatarGroup or indicator for remaining items */}
                                {/* {row.ui_site_name.length > 2 && (
                                        <AvatarGroup
                                          total={row.ui_site_name.length - 2}
                                        />
                                      )}
                                    </>
                                  )} */}
                              </div>
                            </>
                          ) : key === "integration_status" ? (
                            <>
                              <div className="AlertItems">
                                {row[key] === "Ready" ? (
                                  // SUCCESS ALERT
                                  <Alert
                                    icon={
                                      <CheckCircleOutlineIcon
                                        fontSize="inherit"
                                        sx={{ color: "rgba(30, 70, 32, 1)" }}
                                      />
                                    }
                                    severity="success"
                                    sx={{
                                      border:
                                        "1px solid rgba(46, 125, 50, 0.5)",
                                      padding: "0px 5px",
                                      display: "inline-flex",
                                    }}
                                  >
                                    Ready
                                  </Alert>
                                ) : row[key] === "Not Ready" ? (
                                  // ERROR ALERT
                                  <Alert
                                    icon={
                                      <ErrorOutlineIcon
                                        fontSize="inherit"
                                        sx={{ color: "rgba(95, 33, 32, 1)" }}
                                      />
                                    }
                                    severity="error"
                                    sx={{
                                      border:
                                        "1px solid rgba(211, 47, 47, 0.5)",
                                      padding: "0px 5px",
                                      display: "inline-flex",
                                    }}
                                  >
                                    Not Ready
                                  </Alert>
                                ) : row[key] === "Running" ? (
                                  // Running ALERT
                                  <Alert
                                    icon={
                                      <LoopIcon
                                        style={{
                                          color: "#7B1FA2",
                                          fontSize: "17",
                                        }}
                                      />
                                    }
                                    // severity="error"
                                    sx={{
                                      color: "#7B1FA2",
                                      backgroundColor: "#9C27B01F",
                                      border: "1px solid #7B1FA2",
                                      padding: "0px 5px",
                                      display: "inline-flex",
                                    }}
                                  >
                                    Running
                                  </Alert>
                                ) : row[key] === "Pending" ? (
                                  // Pending ALERT
                                  <Alert
                                    icon={
                                      <PendingOutlinedIcon
                                        sx={{
                                          color: "#014361",
                                          width: "18px",
                                          height: "18px",
                                        }}
                                      />
                                    }
                                    // severity="error"
                                    sx={{
                                      color: "#014361",
                                      backgroundColor: "#E5F6FD",
                                      border: "1px solid #2196F380",
                                      padding: "0px 5px",
                                      display: "inline-flex",
                                    }}
                                  >
                                    Pending
                                  </Alert>
                                ) : (
                                  <></>
                                )}
                              </div>
                            </>
                          ) : key === "stuck_at" ? (
                            <>
                              {" "}
                              {row.additional_information?.step
                                ? capitalizeEachWords(
                                    row.additional_information?.step,
                                  )
                                : ""}
                            </>
                          ) : key === "reason" ? (
                            <>
                              {row.additional_information?.reason ? (
                                <>{row.additional_information.reason}</>
                              ) : (
                                ""
                              )}
                            </>
                          ) : key === "last_run" ? (
                            <>
                              {" "}
                              {row?.additional_information?.last_run
                                ? formatLastRunDate(
                                    row?.additional_information?.last_run,
                                  )
                                : ""}
                            </>
                          ) : key === "action" ? (
                            <>
                              {(() => {
                                const {
                                  integration_status,
                                  additional_information,
                                } = row;

                                const step = additional_information?.step
                                  ? capitalizeEachWords(
                                      additional_information.step,
                                    )
                                  : "";

                                // Helper function to render link or disabled text
                                const renderActionLink = (linkText: string) => {
                                  if (!hasBrandConfigEditPermission) {
                                    return (
                                      <Typography
                                        variant="body1"
                                        fontWeight={500}
                                        color="text.disabled"
                                      >
                                        {linkText}
                                      </Typography>
                                    );
                                  }

                                  return (
                                    <Link
                                      data-tracker="628"
                                      to={{
                                        pathname:
                                          "/operations/brand_configuration_report/brand_properties",
                                        search: (() => {
                                          const newParams = new URLSearchParams(
                                            location.search,
                                          );
                                          newParams.set(
                                            "brandSharedUid",
                                            row.brand_shared_uid,
                                          );
                                          newParams.set(
                                            "bkInterfaceSharedUid",
                                            row.backend_interface_shared_uid,
                                          );
                                          newParams.set(
                                            "brandId",
                                            row.brand_id,
                                          );
                                          newParams.set("step", step);
                                          return `?${newParams.toString()}`;
                                        })(),
                                      }}
                                      style={{
                                        color: "#7B1FA2",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={500}
                                        color="secondary"
                                      >
                                        {linkText}
                                      </Typography>
                                    </Link>
                                  );
                                };

                                // Handle Ready status
                                if (integration_status === "Ready") {
                                  return renderActionLink("Reconfigure");
                                }

                                // Handle Not Ready status
                                // if (integration_status === "Not Ready") {
                                //   const linkText =
                                //     additional_information?.step ===
                                //     "Properties"
                                //       ? "Edit Properties"
                                //       : "Continue Setup";
                                //   return renderActionLink(linkText);
                                // }

                                if (integration_status === "Not Ready") {
                                  if (
                                    additional_information?.step ===
                                    "Properties"
                                  )
                                    return renderActionLink("Edit Properties");
                                }
                                // according to new requirement, clicking on continue setup make API call to run the integration process
                                if (integration_status === "Not Ready") {
                                  if (
                                    additional_information?.step !==
                                    "Properties"
                                  )
                                    return (
                                      <MuiLink
                                        data-tracker="628"
                                        component="button"
                                        variant="body2"
                                        onClick={() =>
                                          handleOverrite(row.brand_id)
                                        }
                                        sx={{
                                          fontWeight: 500,
                                          textTransform: "none",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          color: "#7B1FA2",
                                          "&:hover": {
                                            textDecoration: "none",
                                          },
                                        }}
                                      >
                                        Continue Setup
                                      </MuiLink>
                                    );
                                }

                                // Handle null/undefined/empty status with Properties step
                                if (
                                  (integration_status === null ||
                                    integration_status === "" ||
                                    integration_status === undefined) &&
                                  additional_information?.step === "Properties"
                                ) {
                                  return renderActionLink("Edit Properties");
                                }

                                // Handle Running / Pending status
                                if (
                                  integration_status === "Running" ||
                                  integration_status === "Pending"
                                ) {
                                  return (
                                    <Typography
                                      variant="body1"
                                      fontWeight={500}
                                      color="text.disabled"
                                    >
                                      Work in Progress
                                    </Typography>
                                  );
                                }

                                return null;
                              })()}
                            </>
                          ) : (
                            "No data available"
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No Data Available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredRows.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </Paper>
    </>
  );
};

export default BrandConfigurationReportTableData;
